import React, { useState, useContext, useEffect, Fragment } from "react";
import Container from "Screens/Container";
import { AppContext } from "context/App";
import { Formik, Form } from "formik";
import { UserValidationSchema } from "schemas/User";
import md5 from "crypto-js/md5";
import { useAuth } from "hooks/Auth";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { oauthPut } from "helpers/Request";
import TextInput from "components/FormElements/TextInput";
import Text from "components/FormElements/Text";
import { useNotification } from "hooks/Notification";
import { Link } from "react-router-dom";
import {
  removeOauthToken,
  removeOauthRefreshToken,
  removePublicationUuid,
  removePublicationCode,
} from "helpers/Auth";

export default function ProfileScreen(props) {
    const { setCurrentNav } = useContext(AppContext);
    const auth = useAuth();
    const notification = useNotification();
    const [saving, setSaving] = useState(false);
    const [hasPassword, setHasPassword] = useState(false);
    const [showNewUserWarning, setShowNewUserWarning] = useState(false);

    // const hasPassword = auth.user.warnings.find((m) => m.code === "nopass")
    //     ? false
    //     : true;

    useEffect(() => {
        setCurrentNav("profile");
    }, [setCurrentNav]);

    useEffect(() => {
        setHasPassword(!auth.user.warnings.find((m) => m.code === "nopass"));
    }, [auth.user]);

    return (
        <Container>
            <div className="z-50 max-w-3xl mx-auto rounded-t-md text-center relative">
                <div className="overflow-hidden h-28">
                    <img
                        src={process.env.PUBLIC_URL.concat("/1500x500.png")}
                        alt="Header Background"
                        className="z-0 w-max overflow-hidden rounded-t-md"
                    />
                </div>
                <img
                    className="h-28 w-28 rounded-full border-4 border-white absolute -bottom-10 inset-x-1/2 -ml-14 bg-white"
                    src={"https://www.gravatar.com/avatar/"
                        .concat(md5(auth.user.email))
                        .concat("?s=350")}
                    alt={auth.user.name}
                />
            </div>
            <div className="max-w-3xl mx-auto bg-white rounded-b-md p-4 shadow-sm pt-12">
                {!hasPassword && (
                    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
                        Update your password below.
                    </div>
                )}
                {showNewUserWarning && (
                    <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                        Password updated! Not sure what to do next?
                        <ul className="list-disc list-inside my-2 space-y-1">
                            {auth.user.roles.find(
                                (r) => r.name.toLowerCase() === "ceo users"
                            ) && (
                                <Fragment>
                                    <li>
                                        <a
                                            href={
                                                process.env
                                                    .REACT_APP_CEO3_DOMAIN
                                            }
                                            target="_blank"
                                            className=" text-green-600 underline"
                                            rel="noreferrer"
                                        >
                                            Log into CEO3
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href={process.env.REACT_APP_ROOT_API.replace(
                                                "{client_code}",
                                                auth.publication.client_code
                                            )}
                                            target="_blank"
                                            className=" text-green-600 underline"
                                            rel="noreferrer"
                                        >
                                            Log into CEO2
                                        </a>
                                    </li>
                                </Fragment>
                            )}
                            <li>
                                <Link
                                    to="/app/dashboard"
                                    className=" text-green-600 underline"
                                >
                                    Head to the dashboard
                                </Link>
                            </li>
                        </ul>
                    </div>
                )}
                <Formik
                    enableReinitialize={true}
                    validationSchema={UserValidationSchema}
                    initialValues={Object.assign({}, auth.user, {
                        password: "",
                        passwordConfirmation: "",
                        licenses: auth.user.licenses.map((item) => {
                            return {
                                label: item.name,
                                value: item.uuid,
                            };
                        }),
                    })}
                    onSubmit={(values) => {
                        setSaving(true);
                        oauthPut("/api/v2/me", values)
                            .then((data) => {
                                setSaving(false);
                                setShowNewUserWarning(!hasPassword);
                                setHasPassword(true);
                                setTimeout(() => {
                                removeOauthToken();
                                removeOauthRefreshToken();
                                removePublicationUuid();
                                removePublicationCode();
                                window.location.href = data.location;
                                }, 1);
                            })
                            .catch((e) => {
                                setSaving(false);
                                notification.show(
                                    "There was an error updating your profile."
                                );
                            });
                    }}
                >
                    {(props) => (
                        <Form className="space-y-8">
                            <Text
                                label="User UUID"
                                id="uuid"
                                name="uuid"
                                value={auth.user.uuid}
                            />
                            <TextInput
                                label="Name"
                                id="name"
                                name="name"
                                type="text"
                            />

                            <TextInput
                                label="Email"
                                id="email"
                                name="email"
                                type="email"
                            />

                            <TextInput
                                label="Password"
                                id="password"
                                name="password"
                                type="password"
                                isError={!hasPassword}
                                errorText={
                                    !hasPassword ? "A password is required" : ""
                                }
                            />
                            <TextInput
                                label="Retype Password"
                                id="passwordConfirmation"
                                name="passwordConfirmation"
                                type="password"
                            />

                            <div className="flex justify-end">
                                <SecondaryButton type="button">
                                    Cancel
                                </SecondaryButton>
                                <PrimaryButton
                                    type="submit"
                                    className="ml-3"
                                    blocking={saving}
                                >
                                    Save
                                </PrimaryButton>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
}
